import { useEffect, useMemo, useRef, useState } from 'react';
import type { EventKind, Item, WorklogEvent } from '../worklog/types';
import { scopeItemsToContract } from '../worklog/types';
import { DEFAULT_KIND_VISIBILITY, KIND_LABELS, VIEW_MODES, eventKind } from '../worklog/eventKind';
import { effectiveTag, eventHash, eventTagPrefix, tagSource } from '../worklog/tags';
import { eventBodyText } from '../worklog/eventBody';
import { fmtDate, fmtTime, eventDate, stripTag } from '../worklog/format';
import { useWorklog } from '../state/WorklogContext';
import { useStatus } from '../state/StatusContext';
import { buildCorrectionIndex, correctionFor } from '../worklog/corrections';
import { deriveTasksFromItems } from '../worklog/groups';

export interface WorklogScope {
  tags: string[] | null;
}

function eventId(e: WorklogEvent): string {
  return String(e.ntp?.epoch || '') + '-' + (e.event || '');
}

interface FilterState {
  search: string;
  visibility: Record<EventKind, boolean>;
  view: 'day' | 'list' | 'session';
  expanded: Set<string>;
  kindPopupOpen: boolean;
  /** Intersect: only events with a URL (standalone link or --url payload). */
  hasUrlOnly: boolean;
}

function defaultFilterState(): FilterState {
  return {
    search: '',
    visibility: { ...DEFAULT_KIND_VISIBILITY },
    view: 'day',
    expanded: new Set<string>(),
    kindPopupOpen: false,
    hasUrlOnly: false,
  };
}

function eventHasUrl(e: WorklogEvent): boolean {
  return e.event === 'link' || (typeof e.url === 'string' && e.url.length > 0);
}

export function WorklogSection({ scopeId, scope }: { scopeId: string; scope: WorklogScope }) {
  const { state } = useWorklog();
  const [filter, setFilter] = useState<FilterState>(defaultFilterState);

  // Reset filters when contract changes.
  useEffect(() => {
    setFilter(defaultFilterState());
  }, [state.activeContract, scopeId]);

  if (state.worklog.cache === 'loading') {
    return (
      <div className="worklog">
        <div className="worklog-empty">loading worklog...</div>
      </div>
    );
  }
  if (state.worklog.cache === 'error') {
    return (
      <div className="worklog">
        <div className="worklog-empty">worklog unavailable: {state.worklog.error || ''}</div>
      </div>
    );
  }

  return (
    <div className="worklog">
      <WorklogToolbar
        events={state.worklog.events}
        scope={scope}
        filter={filter}
        setFilter={setFilter}
      />
      <EventList
        events={state.worklog.events}
        scope={scope}
        filter={filter}
        overlayTags={state.overlay.tags}
        toggleExpanded={(id) => {
          setFilter(prev => {
            const next = new Set(prev.expanded);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return { ...prev, expanded: next };
          });
        }}
      />
    </div>
  );
}

function filterEvents(events: WorklogEvent[], scope: WorklogScope, filter: FilterState, overlayTags: Record<string, string>) {
  const search = filter.search.trim().toLowerCase();
  return events.filter(e => {
    const kind = eventKind(e);
    if (!filter.visibility[kind]) return false;

    if (filter.hasUrlOnly && !eventHasUrl(e)) return false;

    if (scope.tags && scope.tags.length) {
      const tag = effectiveTag(e, overlayTags);
      if (!tag || !scope.tags.includes(tag)) return false;
    }

    if (search) {
      const fields = [e.note, e.task, e.active_task, e.url, e.contract, e.reason]
        .filter(Boolean).join(' ').toLowerCase();
      if (!fields.includes(search)) return false;
    }

    return true;
  });
}

function WorklogToolbar({
  events,
  scope,
  filter,
  setFilter,
}: {
  events: WorklogEvent[];
  scope: WorklogScope;
  filter: FilterState;
  setFilter: (next: FilterState | ((prev: FilterState) => FilterState)) => void;
}) {
  // Counts per kind for current scope (before kind-visibility filter, scope.tags applied).
  const counts = useMemo(() => {
    const c: Partial<Record<EventKind, number>> = {};
    for (const e of events) {
      if (scope.tags && scope.tags.length) {
        const tag = eventTagPrefix(e);
        if (!tag || !scope.tags.includes(tag)) continue;
      }
      const k = eventKind(e);
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [events, scope.tags]);

  // Count of URL-bearing events in the same scope (independent of kind
  // visibility, so the chip's badge reflects what's available to filter to).
  const urlCount = useMemo(() => {
    let n = 0;
    for (const e of events) {
      if (scope.tags && scope.tags.length) {
        const tag = eventTagPrefix(e);
        if (!tag || !scope.tags.includes(tag)) continue;
      }
      if (eventHasUrl(e)) n++;
    }
    return n;
  }, [events, scope.tags]);

  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filter.kindPopupOpen) return;
    function onDocClick(ev: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(ev.target as Node)) {
        setFilter(prev => ({ ...prev, kindPopupOpen: false }));
      }
    }
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setFilter(prev => ({ ...prev, kindPopupOpen: false }));
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [filter.kindPopupOpen, setFilter]);

  const totalKinds = Object.keys(KIND_LABELS).length;
  const activeKinds = Object.values(filter.visibility).filter(Boolean).length;
  const badge = activeKinds < totalKinds ? <span className="kf-badge">{activeKinds}/{totalKinds}</span> : null;

  return (
    <div className="worklog-toolbar">
      <div className="row1">
        <div className="search-wrap">
          <input
            type="text"
            className="search-input"
            placeholder="Search note text, tasks, urls, reasons..."
            value={filter.search}
            onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
          />
          <div className="kf-wrap" ref={wrapRef}>
            <button
              type="button"
              className="kf-btn"
              title="Filter event types"
              aria-haspopup="true"
              aria-expanded={filter.kindPopupOpen}
              onClick={() => setFilter(prev => ({ ...prev, kindPopupOpen: !prev.kindPopupOpen }))}
            >
              <svg className="kf-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M3 6h18M7 12h10M11 18h2" />
              </svg>
              {badge}
            </button>
            <div className="kf-popup" hidden={!filter.kindPopupOpen}>
              {(Object.keys(KIND_LABELS) as EventKind[]).map(kind => (
                <label key={kind} className={'kf-row cat-' + kind}>
                  <input
                    type="checkbox"
                    checked={!!filter.visibility[kind]}
                    onChange={() => setFilter(prev => ({
                      ...prev,
                      visibility: { ...prev.visibility, [kind]: !prev.visibility[kind] },
                    }))}
                  />
                  <span className="kf-swatch" />
                  <span className="kf-label">{KIND_LABELS[kind]}</span>
                  <span className="kf-count">{counts[kind] || 0}</span>
                </label>
              ))}
              <div className="kf-divider" />
              <label className="kf-row kf-row-extra">
                <input
                  type="checkbox"
                  checked={filter.hasUrlOnly}
                  onChange={() => setFilter(prev => ({ ...prev, hasUrlOnly: !prev.hasUrlOnly }))}
                />
                <span className="kf-swatch kf-swatch-url" />
                <span className="kf-label">Has URL only<br /><span className="kf-sub">standalone link or --url payload</span></span>
                <span className="kf-count">{urlCount}</span>
              </label>
            </div>
          </div>
        </div>
        <select
          value={filter.view}
          onChange={(e) => setFilter(prev => ({ ...prev, view: e.target.value as FilterState['view'] }))}
        >
          {VIEW_MODES.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
        </select>
      </div>
    </div>
  );
}

function EventList({
  events,
  scope,
  filter,
  overlayTags,
  toggleExpanded,
}: {
  events: WorklogEvent[];
  scope: WorklogScope;
  filter: FilterState;
  overlayTags: Record<string, string>;
  toggleExpanded: (id: string) => void;
}) {
  const filtered = useMemo(
    () => filterEvents(events, scope, filter, overlayTags),
    [events, scope, filter, overlayTags],
  );
  const correctionIndex = useMemo(() => buildCorrectionIndex(events), [events]);

  if (!filtered.length) {
    return <div className="worklog-empty">no matching events</div>;
  }

  if (filter.view === 'list') {
    return (
      <div className="worklog-list">
        {filtered.slice().reverse().map(e => (
          <EventCard
            key={eventId(e)}
            event={e}
            expanded={filter.expanded.has(eventId(e))}
            overlayTags={overlayTags}
            correctionIndex={correctionIndex}
            onToggle={toggleExpanded}
          />
        ))}
      </div>
    );
  }

  if (filter.view === 'day') {
    const groups = new Map<string, WorklogEvent[]>();
    const order: string[] = [];
    for (const e of filtered) {
      const d = eventDate(e);
      const key = d ? d.toISOString().slice(0, 10) : 'unknown';
      if (!groups.has(key)) { groups.set(key, []); order.push(key); }
      groups.get(key)!.push(e);
    }
    return (
      <>
        {order.slice().reverse().map(key => {
          const list = groups.get(key)!;
          const label = key === 'unknown' ? 'unknown date' : fmtDate(new Date(key));
          return (
            <div className="worklog-list" key={key}>
              <div className="worklog-day-header">{label} ({list.length})</div>
              {list.slice().reverse().map(e => (
                <EventCard
                  key={eventId(e)}
                  event={e}
                  expanded={filter.expanded.has(eventId(e))}
                  overlayTags={overlayTags}
                  correctionIndex={correctionIndex}
                  onToggle={toggleExpanded}
                />
              ))}
            </div>
          );
        })}
      </>
    );
  }

  // session view
  type Block = { events: WorklogEvent[]; startEvent?: WorklogEvent; stopEvent?: WorklogEvent; ungrouped?: boolean };
  const blocks: Block[] = [];
  let current: Block | null = null;
  for (const e of filtered) {
    if (e.event === 'start') {
      if (current) blocks.push(current);
      current = { startEvent: e, events: [e] };
    } else if (e.event === 'stop' && current) {
      current.events.push(e);
      current.stopEvent = e;
      blocks.push(current);
      current = null;
    } else if (current) {
      current.events.push(e);
    } else {
      const last = blocks[blocks.length - 1];
      if (!last || !last.ungrouped) blocks.push({ ungrouped: true, events: [] });
      blocks[blocks.length - 1].events.push(e);
    }
  }
  if (current) blocks.push(current);

  return (
    <>
      {blocks.slice().reverse().map((b, idx) => {
        const items = b.events.slice().reverse().map(e => (
          <EventCard
            key={eventId(e)}
            event={e}
            expanded={filter.expanded.has(eventId(e))}
            overlayTags={overlayTags}
            correctionIndex={correctionIndex}
            onToggle={toggleExpanded}
          />
        ));
        if (b.ungrouped) {
          return (
            <div className="worklog-list" key={'ungrouped-' + idx}>
              <div className="worklog-day-header">unscoped events ({b.events.length})</div>
              {items}
            </div>
          );
        }
        const task = (b.startEvent && b.startEvent.task) || '(unspecified)';
        const startD = eventDate(b.startEvent!);
        let sessionS = 0;
        for (const ev of b.events) if (ev.event === 'block' && typeof ev.seconds === 'number') sessionS += ev.seconds;
        const dur = b.stopEvent
          ? '  (' + (sessionS > 0 ? formatSec(sessionS) : '0s') + ')'
          : '  (open)';
        return (
          <div className="session-block" key={'session-' + idx}>
            <div className="session-block-header">{task}  -  {fmtDate(startD)} {fmtTime(startD)}{dur}</div>
            <div className="session-block-body">{items}</div>
          </div>
        );
      })}
    </>
  );
}

function formatSec(s: number): string {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h) return h + 'h ' + m + 'm';
  if (m) return m + 'm';
  return sec + 's';
}

function EventCard({
  event: e,
  expanded,
  overlayTags,
  correctionIndex,
  onToggle,
}: {
  event: WorklogEvent;
  expanded: boolean;
  overlayTags: Record<string, string>;
  correctionIndex: ReturnType<typeof buildCorrectionIndex>;
  onToggle: (id: string) => void;
}) {
  const d = eventDate(e);
  const tag = effectiveTag(e, overlayTags);
  const source = tagSource(e, overlayTags);
  const cat = (e.category || '').toLowerCase();
  const eid = eventId(e);
  const kind = eventKind(e);

  const myEpoch = e.ntp?.epoch;
  const correction = (e.event === 'note' || e.event === 'NOTE') ? correctionFor(correctionIndex, myEpoch) : null;
  const tagsArr: string[] = Array.isArray(e.tags) ? (e.tags as string[]).filter(Boolean) : [];
  const body = correction ? stripTag(correction.note || '') : eventBodyText(e);
  // Referenced URL: any event other than `link` that carries a `url` field
  // (e.g. a consideration logged with --url). The `link` event already shows
  // its url inline via eventBodyText.
  const referencedUrl = (e.event !== 'link' && typeof e.url === 'string' && e.url) ? e.url : null;

  return (
    <div
      className={
        'event kind-' + kind
        + (expanded ? ' expanded' : '')
        + (correction ? ' corrected' : '')
        + (referencedUrl ? ' has-url' : '')
      }
      onClick={() => onToggle(eid)}
    >
      <div className="event-row">
        <span className="event-time">{fmtTime(d)}</span>
        <span className="event-type">{e.event}</span>
        {tag ? (
          <span
            className={source === 'overlay' ? 'event-tag overlay' : 'event-tag'}
            title={source === 'overlay' ? 'manually tagged' : 'tagged via CLI prefix'}
          >
            [{tag}]
          </span>
        ) : null}
        {cat && ['progress', 'client', 'consideration', 'dashboard', 'link'].includes(cat) ? (
          <span className={'event-cat cat-' + cat}>{cat}</span>
        ) : null}
        {correction ? <span className="event-cat cat-correct" title="superseded by a later correct event">corrected</span> : null}
        <span className={expanded ? 'event-text' : 'event-text truncate'}>{body}</span>
        {referencedUrl ? (
          <a
            className="event-ref-url"
            href={referencedUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(ev) => ev.stopPropagation()}
            title={referencedUrl}
          >
            link
          </a>
        ) : null}
        {tagsArr.map((t, i) => <span key={i} className="event-plustag">+{t}</span>)}
      </div>
      {expanded ? (
        <div className="event-detail">
          {e.event === 'revise' ? <ReviseDetail event={e} /> : null}
          <TagEditor event={e} overlayTags={overlayTags} />
          <details>
            <summary>raw event</summary>
            <pre>{JSON.stringify(e, null, 2)}</pre>
          </details>
        </div>
      ) : null}
    </div>
  );
}

function ReviseDetail({ event }: { event: WorklogEvent }) {
  const title = typeof event.title === 'string' ? event.title : '';
  const before = typeof event.before === 'string' ? event.before : '';
  const after = typeof event.after === 'string' ? event.after : '';
  const titleTags = Array.isArray(event.title_tags) ? (event.title_tags as string[]) : [];
  const beforeTags = Array.isArray(event.before_tags) ? (event.before_tags as string[]) : [];
  const afterTags = Array.isArray(event.after_tags) ? (event.after_tags as string[]) : [];
  return (
    <div className="revise-detail">
      {title ? (
        <div className="revise-title">
          <span className="revise-label">title</span>
          <span className="revise-text">{title}</span>
          {titleTags.length > 0 ? (
            <span className="revise-tags">{titleTags.map((t, i) => <span key={i} className="event-plustag">+{t}</span>)}</span>
          ) : null}
        </div>
      ) : null}
      <div className="revise-pair">
        <div className="revise-side revise-before">
          <span className="revise-label">before</span>
          <pre className="revise-text">{before}</pre>
          {beforeTags.length > 0 ? (
            <div className="revise-tags">{beforeTags.map((t, i) => <span key={i} className="event-plustag">+{t}</span>)}</div>
          ) : null}
        </div>
        <div className="revise-arrow" aria-hidden="true">-&gt;</div>
        <div className="revise-side revise-after">
          <span className="revise-label">after</span>
          <pre className="revise-text">{after}</pre>
          {afterTags.length > 0 ? (
            <div className="revise-tags">{afterTags.map((t, i) => <span key={i} className="event-plustag">+{t}</span>)}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function TagEditor({ event, overlayTags }: { event: WorklogEvent; overlayTags: Record<string, string> }) {
  const { state, setEventTag } = useWorklog();
  const { snapshot } = useStatus();
  const prefix = eventTagPrefix(event);
  const hash = eventHash(event);
  const current = overlayTags[hash] || '';
  const tasks = useMemo(() => {
    const snapItems = (snapshot?.items ?? []).map(it => ({
      id: it.id, label: it.label, status: it.status,
      block_reason: it.block_reason, estimate_minutes: it.estimate_minutes ?? null,
      priority: it.priority ?? null, actual_seconds: it.actual_seconds_derived,
      contract: it.contract, created_at: it.created_at,
      completed_at: it.completed_at ?? null, last_used_at: it.last_used_at ?? null,
    } as Item));
    return deriveTasksFromItems(scopeItemsToContract(snapItems, state.activeContract));
  }, [snapshot, state.activeContract]);

  if (prefix) {
    return (
      <div className="event-tag-editor locked" onClick={(e) => e.stopPropagation()}>
        <span className="tle-label">Tag</span>
        <code>[{prefix}]</code>
        <span className="tle-source">from CLI prefix in note text - edit by changing the note</span>
      </div>
    );
  }

  return (
    <div className="event-tag-editor" onClick={(e) => e.stopPropagation()}>
      <span className="tle-label">Tag</span>
      <select
        value={current}
        onChange={(e) => setEventTag(hash, e.target.value || null)}
      >
        <option value="">(no tag)</option>
        {tasks.map(t => {
          if (!t.items || !t.items.length) return null;
          return (
            <optgroup label={t.title} key={t.id}>
              {t.items.map(i => {
                const trunc = i.text.length > 60 ? i.text.slice(0, 60) + '...' : i.text;
                return (
                  <option value={i.id} key={i.id}>{i.id} - {trunc}</option>
                );
              })}
            </optgroup>
          );
        })}
      </select>
      <span className="tle-source">
        {current ? 'manually tagged - persists in KV across devices' : 'retroactive tag - persists in KV across devices'}
      </span>
    </div>
  );
}
